import { Button } from '@monksflow/monks-ui';
import { AlertCircle, Check, Plus, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type RunResultVariant = 'ready' | 'failed';

interface RunResultLeftRailProps {
  variant: RunResultVariant;
  onNewTest?: () => void;
  onRetry?: () => void;
  retryPending?: boolean;
}

const VARIANTS = {
  ready: {
    Icon: Check,
    iconClassName: 'text-green-600 dark:text-positive-foreground',
    wrapperClassName: 'bg-green-100 dark:bg-positive',
    titleKey: 'contentTest.runResultLeftRail.yourReportIsReady',
  },
  failed: {
    Icon: AlertCircle,
    iconClassName: 'text-destructive',
    wrapperClassName: 'bg-destructive/10',
    titleKey: 'contentTest.runResultLeftRail.runFailed',
    subtitleKey: 'contentTest.runResultLeftRail.weCouldnTCompleteThis',
  },
} as const satisfies Record<
  RunResultVariant,
  {
    Icon: typeof Check;
    iconClassName: string;
    wrapperClassName: string;
    titleKey: string;
    subtitleKey?: string;
  }
>;

export const RunResultLeftRail = ({
  variant,
  onNewTest,
  onRetry,
  retryPending = false,
}: RunResultLeftRailProps) => {
  const { t } = useTranslation('contentTest');

  const config = VARIANTS[variant];
  const { Icon, iconClassName, wrapperClassName, titleKey } = config;
  const subtitleKey = 'subtitleKey' in config ? config.subtitleKey : undefined;

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 text-center">
      <div className={`flex items-center justify-center w-12 h-12 rounded ${wrapperClassName}`}>
        <Icon size={24} className={iconClassName} />
      </div>
      <p className="text-xl font-medium text-foreground tracking-tight">{t(titleKey)}</p>
      {subtitleKey && <p className="text-sm text-muted-foreground">{t(subtitleKey)}</p>}
      {onRetry && (
        <Button
          onClick={onRetry}
          disabled={retryPending}
          className="inline-flex items-center gap-2"
        >
          <RotateCcw size={16} />
          {t('contentTest.runResultLeftRail.retry')}
        </Button>
      )}
      {onNewTest && (
        <Button onClick={onNewTest} variant="outline" className="inline-flex items-center gap-2">
          {t('contentTest.runResultLeftRail.newContentTest')}
          <Plus size={16} />
        </Button>
      )}
    </div>
  );
};

RunResultLeftRail.displayName = 'RunResultLeftRail';
